/**
 * The cutoff as a ring.
 *
 * A countdown in numbers tells you how long is left; the ring tells you how
 * much of the window is gone without being read. The design puts it next to
 * the number rather than instead of it, because at four minutes the number is
 * the part that matters.
 */
export default function CutoffRing({
  fraction,
  size = 64,
  stroke = 7,
  label,
}: {
  /** How much of the ordering window remains, 0–1. */
  fraction: number;
  size?: number;
  stroke?: number;
  /** Short enough to read inside the ring: "19h", "6m", "58%". */
  label: string;
}) {
  const radius = (size - stroke) / 2 - 1;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(1, fraction));

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="var(--color-panel-soft)"
          strokeWidth={stroke}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="var(--color-brand-bright)"
          strokeWidth={stroke}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - clamped)}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      {/* One word, at a size somebody can actually read. A second line in here
          only ever fits at 8px, which is not a size — it is a decoration. */}
      <b className="absolute inset-0 flex items-center justify-center text-[15px] md:text-[16.5px] tracking-[-0.02em] text-ink">
        {label}
      </b>
    </div>
  );
}
